import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import {
  DollarSign,
  TrendingUp,
  Package,
  Percent,
  Calendar,
  Loader2
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/dashboard/stats');

      if (response.data.success) {
        setStats(response.data.data);
      } else {
        throw new Error(response.data.message || 'Veriler alınamadı');
      }
    } catch (requestError) {
      console.error('Dashboard stats error:', requestError);
      let errorMessage = 'Dashboard verileri alınamadı';

      if (requestError.response?.status === 401) {
        errorMessage = 'Oturum süresi dolmuş. Lütfen tekrar giriş yapın.';
      } else if (requestError.response?.data?.message) {
        errorMessage = requestError.response.data.message;
      } else if (requestError.message) {
        errorMessage = requestError.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const handleRefresh = useCallback(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }, []);

  const formatProjectedRevenue = useCallback((amount) => {
    const numValue = typeof amount === 'string' ? parseFloat(amount.replace(/[^\d.-]/g, '')) : (amount || 0);
    const rounded = Math.floor(Number(numValue) / 1000) * 1000;
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(rounded);
  }, []);

  const formatNumber = useCallback((number) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(number);
  }, []);

  const formatTonnage = useCallback((units) => {
    if (!units || Object.keys(units).length === 0) {
      return 'Veri yok';
    }

    return Object.entries(units)
      .filter(([_, value]) => value > 0)
      .map(([unit, value]) => `${formatNumber(value)} ${unit}`)
      .join(' / ');
  }, [formatNumber]);

  const StatCard = useMemo(() => ({ title, value, subtitle, icon: Icon, color = 'blue', onClick }) => {
    const colorClasses = {
      blue: {
        bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
        border: isDark ? 'border-blue-800' : 'border-blue-200',
        icon: isDark ? 'text-blue-400' : 'text-blue-600',
        text: isDark ? 'text-blue-100' : 'text-blue-900'
      },
      green: {
        bg: isDark ? 'bg-green-900/20' : 'bg-green-50',
        border: isDark ? 'border-green-800' : 'border-green-200',
        icon: isDark ? 'text-green-400' : 'text-green-600',
        text: isDark ? 'text-green-100' : 'text-green-900'
      },
      purple: {
        bg: isDark ? 'bg-purple-900/20' : 'bg-purple-50',
        border: isDark ? 'border-purple-800' : 'border-purple-200',
        icon: isDark ? 'text-purple-400' : 'text-purple-600',
        text: isDark ? 'text-purple-100' : 'text-purple-900'
      },
      orange: {
        bg: isDark ? 'bg-orange-900/20' : 'bg-orange-50',
        border: isDark ? 'border-orange-800' : 'border-orange-200',
        icon: isDark ? 'text-orange-400' : 'text-orange-600',
        text: isDark ? 'text-orange-100' : 'text-orange-900'
      }
    };

    const classes = colorClasses[color];

    return (
      <div
        className={`p-4 sm:p-6 rounded-lg border ${classes.bg} ${classes.border} transition-all duration-200 hover:shadow-lg ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {title}
            </p>
            <p className={`text-xl sm:text-2xl font-bold mt-2 ${classes.text}`}>
              {value}
            </p>
            {subtitle && (
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {subtitle}
              </p>
            )}
          </div>
          <div className={`p-2.5 sm:p-3 rounded-full ${classes.bg}`}>
            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${classes.icon}`} />
          </div>
        </div>
      </div>
    );
  }, [isDark]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">İşletmenizin genel durumunu görüntüleyin</p>
          </div>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 dark:text-blue-400" />
            <p className="text-gray-500 dark:text-gray-400 text-lg mt-4">Dashboard verileri yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">İşletmenizin genel durumunu görüntüleyin</p>
          </div>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 dark:text-red-400 text-lg">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">İşletmenizin genel durumunu görüntüleyin</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Calendar className="h-4 w-4" />
          Yenile
        </button>
      </div>

      {stats?.targetProgress && (
        <div className={`p-3 sm:p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Aylık Hedef {stats.targetProgress.month ? `(${stats.targetProgress.month})` : ''}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Hedef ciro, gerçekleşen ve ay sonu tahmini
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/management/targets')}
              className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
            >
              Hedefleri Düzenle
            </button>
          </div>

          {parseFloat(stats.targetProgress.targetRevenue || 0) <= 0 ? (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Bu ay için hedef girilmemiş. Dashboard tahmini hedefe göre ilerlemeyi gösterebilmesi için hedefleri kaydedin.
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                Gerçekleşen: {formatCurrency(parseFloat(stats.targetProgress.actualRevenue || 0))} · Tahmini: ${formatProjectedRevenue(stats.targetProgress.projectedRevenue)}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Hedef</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    ${formatProjectedRevenue(stats.targetProgress.targetRevenue)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Gerçekleşen</p>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {formatCurrency(parseFloat(stats.targetProgress.actualRevenue))}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Ay Sonu Tahmini</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    ${formatProjectedRevenue(stats.targetProgress.projectedRevenue)}
                  </p>
                </div>
              </div>

              <div className="relative w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-3">
                <div
                  className={`absolute top-0 left-0 h-full rounded-full transition-all duration-300 ${
                    parseFloat(stats.targetProgress.achievementRate) >= 90
                      ? 'bg-green-500'
                      : parseFloat(stats.targetProgress.achievementRate) >= 70
                        ? 'bg-orange-500'
                        : 'bg-red-500'
                  }`}
                  style={{
                    width: `${Math.min(parseFloat(stats.targetProgress.achievementRate), 100)}%`
                  }}
                />
              </div>

              <div className="flex items-center justify-between gap-2 mt-2">
                <p className={`text-xs font-semibold ${
                  parseFloat(stats.targetProgress.achievementRate) >= 90
                    ? 'text-green-600 dark:text-green-400'
                    : parseFloat(stats.targetProgress.achievementRate) >= 70
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-red-600 dark:text-red-400'
                }`}>
                  Gerçekleşme %{stats.targetProgress.achievementRate}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Gün {stats.targetProgress.currentDay}/{stats.targetProgress.daysInMonth}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Günlük Satış"
          value={formatCurrency(parseFloat(stats?.dailySales?.amount || 0))}
          subtitle={`${stats?.dailySales?.date || 'Bugün'}`}
          icon={DollarSign}
          color="green"
          onClick={() => navigate('/reports/sales?tab=customer&dateRange=today')}
        />

        <StatCard
          title="Aylık Ciro"
          value={formatCurrency(parseFloat(stats?.monthlyRevenue?.amount || 0))}
          subtitle={`${stats?.monthlyRevenue?.month || 'Bu Ay'}`}
          icon={TrendingUp}
          color="blue"
        />

        <StatCard
          title="Aylık Tonaj"
          value={formatTonnage(stats?.monthlyTonnage?.units)}
          subtitle={`${stats?.monthlyTonnage?.month || 'Bu Ay'}`}
          icon={Package}
          color="purple"
        />

        <StatCard
          title="Aylık İade Oranı"
          value={`%${stats?.monthlyReturnRate?.rate || '0.00'}`}
          subtitle={`${stats?.monthlyReturnRate?.month || 'Bu Ay'}`}
          icon={Percent}
          color="orange"
        />
      </div>

      {stats?.exchangeRates && (
        <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Güncel Döviz Kurları
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">USD/TRY:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {stats.exchangeRates.USDTRY?.sell?.toFixed(2) || 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">EUR/TRY:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {stats.exchangeRates.EURTRY?.sell?.toFixed(2) || 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">EUR/USD:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {stats.exchangeRates.EURUSD?.sell?.toFixed(2) || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
